import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Clock, BookOpen, MessagesSquare, Target, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { ThemePicker } from "@/components/ThemePicker";
import { ReviewReminder } from "@/components/ReviewReminder";
import { DailyGoal } from "@/components/DailyGoal";
import { RecapCard } from "@/components/RecapCard";
import { AssignedLessons } from "@/components/ChefLessons";
import { TeamReviewCard } from "@/components/TeamReview";
import { InstallCard } from "@/components/InstallCard";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LevelBadge } from "@/components/LevelBadge";
import { ThemeIcon } from "@/components/ThemeIcon";
import { Skeleton } from "@/components/ui/skeleton";

const STEPS = [
  { icon: BookOpen, label: "4 mots" },
  { icon: MessagesSquare, label: "Dialogue" },
  { icon: Target, label: "Situation" },
];

function LessonLoading() {
  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 p-6 space-y-4" data-testid="home-lesson-loading">
      <div className="flex items-center gap-2 text-slate-600 font-semibold">
        <Loader2 className="w-5 h-5 animate-spin text-orange-500" /> Préparation de votre leçon…
      </div>
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [stats, setStats] = useState(null);
  const [creating, setCreating] = useState(false);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    api.get("/progress").then((r) => setStats(r.data)).catch(() => {});
    api.get("/vocabulary").then((r) => setReviewCount(r.data.filter((w) => w.to_review).length)).catch(() => {});
    api.get("/lessons/today").then((r) => setLesson(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const newLesson = async (theme) => {
    setCreating(theme || "surprise");
    try {
      const { data } = await api.post("/lessons/new", theme ? { theme } : {});
      nav(`/lecon/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between rise">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">Salut</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase leading-none" data-testid="home-greeting">
            {user.name}
          </h1>
        </div>
        <div className="flex items-center gap-1.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-3 py-1.5 font-bold" data-testid="home-streak-badge">
          <Flame className="w-5 h-5" /> {stats?.streak ?? 0} j
        </div>
      </div>

      <DailyGoal today={stats?.today} />
      <AssignedLessons />
      <TeamReviewCard />
      <RecapCard />
      <InstallCard dismissible testId="home-install-card" />

      <ReviewReminder count={reviewCount} />

      <section className="rise rise-1">
        <p className="text-xs font-bold uppercase tracking-widest text-orange-600 mb-2">Leçon du jour</p>
        {!lesson ? (
          <LessonLoading />
        ) : (
          <div className="rounded-2xl bg-slate-900 text-white overflow-hidden shadow-xl" data-testid="home-hero-lesson-card">
            <div className="hazard-stripe h-2" />
            <div className="p-6 space-y-5 relative">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-orange-400 font-bold uppercase text-sm">
                  <ThemeIcon theme={lesson.theme} className="w-5 h-5" /> {lesson.theme}
                </span>
                <LevelBadge level={lesson.level} testId="home-current-level-badge" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase leading-tight" data-testid="home-lesson-title">
                {lesson.title}
              </h2>
              <div className="grid grid-cols-3 gap-2">
                {STEPS.map(({ icon: Icon, label }) => (
                  <div key={label} className="bg-white/10 rounded-xl p-3 flex flex-col items-center gap-1 text-sm font-semibold">
                    <Icon className="w-6 h-6 text-amber-400" /> {label}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-slate-300 text-sm">
                  <Clock className="w-4 h-4" /> 5 min
                </span>
                {lesson.completed ? (
                  <span className="flex items-center gap-2 text-emerald-400 font-bold" data-testid="home-lesson-done">
                    <CheckCircle2 className="w-5 h-5" /> Terminée · {lesson.score}/{lesson.total}
                  </span>
                ) : null}
              </div>
              <button
                onClick={() => nav(`/lecon/${lesson.id}`)}
                data-testid="home-start-lesson-btn"
                className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[.98] font-display text-xl font-bold uppercase flex items-center justify-center gap-2 transition-[background-color,transform]"
              >
                {lesson.completed ? "Revoir la leçon" : "Commencer"} <ArrowRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </section>

      {lesson && <ThemePicker busy={creating} onPick={newLesson} />}

      {stats && (
        <section className="grid grid-cols-3 gap-3 rise rise-2" data-testid="home-stats">
          {[
            ["Leçons", stats.lessons_completed, "home-stat-lessons"],
            ["Réussite", `${stats.average}%`, "home-stat-average"],
            ["Jours", stats.days_active, "home-stat-days"],
          ].map(([l, v, id]) => (
            <div key={l} className="bg-white rounded-xl border-2 border-slate-200 p-4">
              <div className="font-display text-3xl font-bold" data-testid={id}>{v}</div>
              <div className="text-xs font-bold uppercase text-slate-500">{l}</div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
